import { getNarrowCurrencySymbol } from "@redotech/money/currencies";
import { useHandler } from "@redotech/react-util/hook";
import { getShopCurrency } from "@redotech/redo-billing-common/team-currency";
import { SegmentFieldUnit } from "@redotech/redo-customer-segments-common/marketing/segments/segment-data-structures";
import { NumericCompareOperator } from "@redotech/redo-customer-segments-common/segments/segment-where-condition";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { memo, useContext, useState } from "react";
import * as segmentBuilderCss from "../segment-builder.module.css";
import { NumericCompareInProgress } from "../segment-in-progress";
import {
  PresenceAwareOperatorSelect,
  PresenceOperators,
} from "./presence-operators";

const numericCompareOperatorToFriendly: Record<NumericCompareOperator, string> =
  {
    [NumericCompareOperator.EQ]: "equals",
    [NumericCompareOperator.GT]: "is greater than",
    [NumericCompareOperator.LT]: "is less than",
    [NumericCompareOperator.GTE]: "is at least",
    [NumericCompareOperator.LTE]: "is at most",
    [NumericCompareOperator.NEQ]: "does not equal",
  };

const numericCompareOperatorOptions: RedoListItem<NumericCompareOperator>[] =
  Object.values(NumericCompareOperator).map((operator) => ({
    id: operator,
    type: "text",
    text: numericCompareOperatorToFriendly[operator],
    value: operator,
  }));

export const NumericFilter = memo(function NumericFilter({
  comparison,
  setComparison,
  presence,
  unit,
}: {
  comparison: NumericCompareInProgress;
  setComparison: (condition: NumericCompareInProgress) => void;
  presence?: PresenceOperators;
  unit?: SegmentFieldUnit;
}) {
  const team = useContext(TeamContext);
  const currency = getShopCurrency({ team });

  return (
    <>
      <PresenceAwareOperatorSelect
        operator={comparison.operator}
        operatorItems={numericCompareOperatorOptions}
        presence={presence}
        setOperator={(operator) => {
          setComparison({ ...comparison, operator });
        }}
      />
      {!presence?.operator && (
        <NumericFilterInput
          allowDecimal={unit === "currency"}
          dataTestId="numeric-filter-input"
          prefix={
            unit === "currency" && currency
              ? getNarrowCurrencySymbol(currency)
              : undefined
          }
          setValue={(value) => {
            setComparison({ ...comparison, value });
          }}
          suffix={unit === "percent" ? "%" : undefined}
          value={comparison.value}
        />
      )}
    </>
  );
});

export const NumericFilterInput = memo(function NumericFilterInput({
  value,
  setValue,
  maxDigits,
  allowNegative,
  dataTestId,
  prefix,
  suffix,
  allowDecimal = false,
}: {
  value: number | undefined;
  setValue: (value: number | undefined) => void;
  maxDigits?: number;
  allowNegative?: boolean;
  dataTestId?: string;
  prefix?: string;
  suffix?: string;
  /** Off by default: timeframe and radius fields must stay whole numbers. */
  allowDecimal?: boolean;
}) {
  const digits = allowDecimal ? "\\d*\\.?\\d*" : "\\d*";
  const pattern = allowNegative ? `-?${digits}` : digits;

  /** Held while editing so a trailing "." survives until the cents are typed. */
  const [draft, setDraft] = useState<string | undefined>(undefined);

  const setNumber = useHandler((raw: string) => {
    if (allowDecimal) {
      setDraft(raw);
    }
    const parsed = allowDecimal ? parseFloat(raw) : parseInt(raw, 10);
    setValue(isNaN(parsed) ? undefined : parsed);
  });

  return (
    <RedoTextInput
      adornmentVariant="inline"
      className={segmentBuilderCss.relativeUnitValueSelector}
      constrainToInputLength
      dataTestId={dataTestId}
      htmlPattern={pattern}
      maxLength={maxDigits}
      onBlur={() => setDraft(undefined)}
      prefix={prefix}
      setValue={setNumber}
      suffix={suffix}
      value={draft ?? `${value ?? ""}`}
    />
  );
});
